import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';

/**
 * @description Grupo de acciones de la última columna ("Acciones") de las tablas
 * de listado del panel. Reemplaza los `<button>` sueltos que cada página escribía
 * a mano (Projects.js, WorkOrders.js, Equipment.js, Users.js, MaintenanceReports.js
 * y MaintenanceOrders.js — hallazgo de auditoría "Claude Design" #07).
 *
 * Jerarquía visual: la(s) acción(es) esperada(s) —editar, ver detalle, asignar—
 * se muestran como botón(es) con tinte teal (etiqueta + ícono). La acción
 * destructiva, cuando existe, es la excepción: un botón solo-ícono neutro que
 * recién se pone rojo al pasar el mouse, para que no compita por atención en
 * cada fila. El rojo sólido queda reservado exclusivamente al botón de
 * confirmación dentro del Modal (ver Projects.js/Equipment.js/Users.js/
 * MaintenanceOrders.js), nunca aparece en la fila.
 *
 * Dos formas de uso, según lo que necesite la página:
 *
 * 1) Caso común (Projects, Equipment, Users): una acción "Editar" + una
 *    destructiva "Eliminar".
 *      <RowActions onEdit={() => openModal(row)} onDelete={() => handleDelete(row.id)} />
 *
 * 2) Caso con acción(es) secundaria(s) distintas de "editar", o más de una
 *    acción no destructiva en la misma fila (WorkOrders tiene "Ver detalle" +
 *    "Editar / Asignar"; MaintenanceReports solo tiene "Ver detalle"): se pasa
 *    la lista explícita `actions`, cada una con su propio ícono y label.
 *      <RowActions actions={[
 *        { key: 'detail', label: 'Ver detalle', icon: Eye, onClick: () => openDetailModal(row) },
 *        { key: 'edit', label: 'Editar / Asignar', icon: Pencil, onClick: () => openModal(row) },
 *      ]} />
 *
 * La acción destructiva es siempre independiente de las anteriores (`onDelete`):
 * en MaintenanceOrders no es "Eliminar" sino "Cancelar" una orden, así que el
 * label y el ícono son configurables (`deleteLabel`, `deleteIcon`) manteniendo
 * el mismo tratamiento visual de "excepción destructiva".
 *
 * @param {Function} [props.onEdit] Atajo para el caso común de una sola acción secundaria.
 * @param {string} [props.editLabel='Editar'] Etiqueta de esa acción.
 * @param {React.ComponentType} [props.editIcon] Ícono de esa acción (lucide-react). Por defecto `Pencil`.
 * @param {Array<{key?: string, label: string, icon: React.ComponentType, onClick: Function}>} [props.actions]
 *   Lista explícita de acciones secundarias. Si se pasa, reemplaza a `onEdit`/`editLabel`/`editIcon`.
 * @param {Function} [props.onDelete] Acción destructiva (eliminar, cancelar, etc.). Si no se pasa, no se renderiza el botón.
 * @param {string} [props.deleteLabel='Eliminar'] Label/aria-label/title del botón destructivo.
 * @param {React.ComponentType} [props.deleteIcon] Ícono del botón destructivo (lucide-react). Por defecto `Trash2`.
 * @param {boolean} [props.disabled=false] Deshabilita todos los botones del grupo (45% opacidad, cursor not-allowed).
 * @returns {JSX.Element | null}
 */
const RowActions = ({
  onEdit,
  editLabel = 'Editar',
  editIcon: EditIcon = Pencil,
  actions,
  onDelete,
  deleteLabel = 'Eliminar',
  deleteIcon: DeleteIcon = Trash2,
  disabled = false,
}) => {
  // Normaliza a una lista de acciones secundarias (tinte teal, con label).
  // `actions` explícito tiene prioridad; si no viene, `onEdit` arma una lista
  // de un solo elemento — es el 90% de los casos (Projects/Equipment/Users).
  const secondaryActions = actions || (onEdit
    ? [{ key: 'edit', label: editLabel, icon: EditIcon, onClick: onEdit }]
    : []);

  // Nada que mostrar (p. ej. MaintenanceOrders oculta el grupo completo cuando
  // la orden ya está CANCELED/COMPLETED) — no renderizar un contenedor vacío.
  if (secondaryActions.length === 0 && !onDelete) {
    return null;
  }

  return (
    <div className="row-actions">
      {secondaryActions.map(({ key, label, icon: Icon, onClick }) => (
        <button
          key={key || label}
          type="button"
          className="row-actions-edit"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          title={label}
        >
          <Icon size={16} strokeWidth={2} aria-hidden="true" />
          {/* El label se oculta en mobile vía CSS (.row-actions-label) — el botón
              pasa a solo-ícono para no partir el grupo en dos líneas. */}
          <span className="row-actions-label">{label}</span>
        </button>
      ))}
      {onDelete && (
        // Botón solo-ícono: neutro en reposo, rojo recién al hacer hover —
        // la excepción destructiva no debe competir por atención en la fila.
        <button
          type="button"
          className="row-actions-delete"
          onClick={onDelete}
          disabled={disabled}
          aria-label={deleteLabel}
          title={deleteLabel}
        >
          <DeleteIcon size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default RowActions;
